import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useCallback, useState, useEffect } from "react";
import {
	useDataProvider,
	useGetIdentity,
	useRedirect,
	useTranslate,
} from "react-admin";
import { useLocation } from "react-router-dom";
import { ModerationEntry } from "./ModerationEntry";
import SectionTitle from "../../utils/SectionTitle";
import styles from "./ModerationList.module.scss";

const ModerationList = () => {
	const [moderationStack, setModerationStack] = useState([]);
	const [currentEntry, setCurrentEntry] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const { data: identity } = useGetIdentity();
	const location = useLocation();
	const redirectTo = useRedirect();
	const translate = useTranslate();
	const dataProvider = useDataProvider();

	const fetchModerationStack = useCallback(async () => {
		setModerationStack([]);
		setCurrentEntry(0);
		setIsLoading(true);

		try {
			const response = await dataProvider.getList("moderation_entries/lock", {
				sort: { field: "created_at", order: "ASC" },
				pagination: { page: 1, perPage: 25 },
				filter: { ...location.state }
			});

			setModerationStack(response.data);
			setIsLoading(false);
		} catch (error) {
			console.log(error);
			setIsLoading(false);
		}
	}, [dataProvider]);

	useEffect(() => {
		if (identity) {
			fetchModerationStack(1);
		} else {
			redirectTo("/moderation_entries/lobby");
		}
	}, [identity, redirectTo]);

	const addSelectionStatusToEntry = (score) => {
		setModerationStack((prevStack) => {
			const newStack = [...prevStack];
			newStack[currentEntry] = {
				...newStack[currentEntry],
				moderable: {
					...newStack[currentEntry].moderable,
					score,
				},
			};
			return newStack;
		});
	};

	const addReformulationToEntry = (reformulation) => {
		setModerationStack((prevStack) => {
			const newStack = [...prevStack];
			newStack[currentEntry] = {
				...newStack[currentEntry],
				moderable: {
					...newStack[currentEntry].moderable,
					reformulated_content: reformulation,
				},
			};
			return newStack;
		});
	};

	const handleUpdate = async (getPreviousContent = false) => {
		if (getPreviousContent) {
			setCurrentEntry((prev) => Math.max(prev - 1, 0));
		} else {
			if (currentEntry === moderationStack.length - 1) {
				await fetchModerationStack(1);
			} else {
				setCurrentEntry((prev) => prev + 1);
			}
		}
	};

	if (!identity || isLoading) {
		return <CircularProgress />;
	}

	return (
		<>
			<SectionTitle
				title={translate("resources.moderation_entries.label.name")}
			/>
			<div className={styles.root}>
				{moderationStack.length === 0 && !isLoading ? (
					<div className={styles.successContainer}>
						<Alert severity="success" className={styles.success}>
							<AlertTitle>Bravo !</AlertTitle>
							{translate(
								"resources.moderation_entries.label.moderation_completed",
							)}
						</Alert>
						<Button onClick={() => fetchModerationStack(1)}>
							{translate("resources.moderation_entries.label.load_more")}
						</Button>
					</div>
				) : (
					<div className={styles.moderationEntryContainer}>
						<ModerationEntry
							userId={identity.id}
							moderationEntry={{
								...moderationStack[currentEntry],
								id: moderationStack[currentEntry].identifier,
							}}
							onUpdate={handleUpdate}
							onUpdateSelection={addSelectionStatusToEntry}
							onReformulate={addReformulationToEntry}
						/>
					</div>
				)}
			</div>
		</>
	);
};

export default ModerationList;
